import {
  DICA_ESTILO,
  DICA_PASSE,
  DICA_PRESSAO,
  HIDRATACAO_MS,
  INTERVALO_MS,
  LISTA_ESTILOS,
  LISTA_FORMACOES,
  LISTA_PASSES,
  LISTA_PRESSOES,
  PAUSA_MS,
  ROTULO_ESTILO,
  ROTULO_PASSE,
  ROTULO_PRESSAO,
  clubePorId,
  decorridoMs,
  fatorPosicao,
  situacaoEm,
  slotsDaFormacao,
  type Clube,
  type Formacao,
  type Jogador,
} from '../core';
import { app, humano, jogoAtual, type EstadoPausa } from './app';
import { dialogo } from './dialogos';
import { posicoesNoCampo, rotuloJogador, shirt, type AlvoArraste, type OrigemArraste } from './escalar';
import { camisaVazia } from './icones';
import { agoraDoServidor } from './relogio';
import { esc } from './util';

/**
 * Janela de substituições com a partida online pausada: a mesma cara do Escalar Time (campo e banco), só que com quem
 * está em campo NESTE minuto. O servidor espera até PAUSA_MS; o técnico troca jogadores e muda a tática e confirma.
 */

function partidaDoClube(clubeId: number) {
  const e = jogoAtual();
  return e.ultimaRodada?.partidas.find((p) => p.mandante === clubeId || p.visitante === clubeId);
}

/** Tempo (ms) que resta da pausa, pelo relógio do servidor. */
export function restoDaPausa(): number {
  const o = app.online;
  const rel = o?.visao?.sala.aoVivo;
  const limite = rel?.pausa?.ate ?? (rel?.intervalo && !rel.intervalo.feito ? rel.intervalo.ate : null) ?? (rel?.hidratacao && !rel.hidratacao.feito ? rel.hidratacao.ate : null);
  if (!o?.visao || limite === null || limite === undefined) return 0;
  return Math.max(0, limite - agoraDoServidor());
}

/** Parada coletiva em andamento (intervalo aos 45' ou pausa para hidratação aos 65': o relógio parou e falta alguém confirmar), se houver. */
export function intervaloAtivo() {
  const rel = app.online?.visao?.sala.aoVivo;
  const iv = rel?.intervalo;
  if (iv && !iv.feito && iv.ate !== null) return { ...iv, hidratacao: false };
  const hid = rel?.hidratacao;
  return hid && !hid.feito && hid.ate !== null ? { ...hid, hidratacao: true } : null;
}

export function iniciarPausa(minuto: number, ate: number, intervalo = false, hidratacao = false, local?: { estilo: string; marcacao: string; ataque: string; passe: string; pressao: string }): void {
  const c = humano();
  const partida = partidaDoClube(c.id);
  if (!partida) return;
  const lado = partida.mandante === c.id ? 0 : 1;
  const sit = situacaoEm(partida.resultado, lado, minuto);
  // a formação em campo: a que o técnico trocou numa pausa anterior ou a que tem exatamente os slots do início
  const formacao = sit.formacao ?? LISTA_FORMACOES.find((x) => mesmosSlots(slotsDaFormacao(x), sit.slots)) ?? null;
  app.pausa = {
    formacaoInicial: formacao,
    formacao,
    perm: sit.slots.map((_, i) => i),
    chave: `${hidratacao ? 'hid' : intervalo ? 'iv' : 'p'}:${minuto}:${ate}`,
    intervalo,
    hidratacao,
    minuto,
    ocupantes: sit.ocupantes,
    slots: sit.slots,
    banco: sit.banco,
    restantes: Math.max(0, 5 - sit.substituicoes),
    pendentes: [],
    selSai: null,
    // jogo solo (local): a tática que o time tem agora na partida, mudada pelas pausas anteriores; online, a do clube
    estilo: (local?.estilo ?? c.tatica.estilo) as EstadoPausa['estilo'],
    marcacao: (local?.marcacao ?? c.tatica.marcacao) as EstadoPausa['marcacao'],
    ataque: (local?.ataque ?? c.tatica.ataque) as EstadoPausa['ataque'],
    passe: (local?.passe ?? c.tatica.passe ?? 'misto') as EstadoPausa['passe'],
    pressao: (local?.pressao ?? c.tatica.pressao ?? 'media') as EstadoPausa['pressao'],
    ...(local ? { local: true } : {}),
  };
  app.dialogo = 'pausa';
}

function jogadorDe(c: Clube, id: number | null): Jogador | undefined {
  return id === null ? undefined : c.elenco.find((j) => j.id === id);
}

const mesmosSlots = (a: string[], b: string[]): boolean => a.length === b.length && a.every((p, i) => p === b[i]);

/** Slots da formação escolhida (se a original não é conhecida, os slots do jogo como estavam). */
function slotsMostrados(st: EstadoPausa): EstadoPausa['slots'] {
  return st.formacao ? slotsDaFormacao(st.formacao) : st.slots;
}

/** Quem está na unidade `u` agora: o reserva que vai entrar no lugar dela, senão o ocupante de antes (null = vazio). */
function ocupanteDaUnidade(st: EstadoPausa, u: number): number | null {
  const original = st.ocupantes[u] ?? null;
  if (original === null) return null;
  return st.pendentes.find((x) => x.saiId === original)?.entraId ?? original;
}

/** Ao trocar de formação, distribui quem está em campo pelos slots novos: cada um vai para o slot mais próximo da sua posição. */
function refazerPerm(st: EstadoPausa, formacao: Formacao): void {
  const c = humano();
  const slots = slotsDaFormacao(formacao);
  const perm: (number | null)[] = slots.map(() => null);
  const usadas = new Set<number>();
  // o goleiro (unidade do slot G) fica no gol
  const uGol = st.slots.indexOf('G');
  const kGol = slots.indexOf('G');
  if (uGol >= 0 && kGol >= 0) {
    perm[kGol] = uGol;
    usadas.add(uGol);
  }
  const pares: { u: number; k: number; nota: number }[] = [];
  st.ocupantes.forEach((_, u) => {
    if (usadas.has(u)) return;
    const j = jogadorDe(c, ocupanteDaUnidade(st, u));
    if (!j) return;
    slots.forEach((s, k) => {
      if (perm[k] === null) pares.push({ u, k, nota: fatorPosicao(j.posicao, s) * 100 + j.overall / 100 });
    });
  });
  pares.sort((a, b) => b.nota - a.nota);
  for (const p of pares) {
    if (perm[p.k] !== null || usadas.has(p.u)) continue;
    perm[p.k] = p.u;
    usadas.add(p.u);
  }
  // quem sobrou (unidades vazias) ocupa os slots que restaram
  const sobras = st.ocupantes.map((_, u) => u).filter((u) => !usadas.has(u));
  st.perm = perm.map((u) => u ?? (sobras.shift() as number));
  st.formacao = formacao;
}

/** Formação e ocupantes de cada slot para o servidor; só quando algo mudou em relação ao que estava em campo. */
export function formacaoParaEnvio(st: EstadoPausa): { nome: Formacao; ordem: (number | null)[] } | undefined {
  if (!st.formacao) return undefined;
  const mudouPerm = st.perm.some((u, k) => u !== k);
  if (st.formacao === st.formacaoInicial && !mudouPerm) return undefined;
  return { nome: st.formacao, ordem: st.perm.map((u) => ocupanteDaUnidade(st, u)) };
}

export function dlgPausa(): string {
  const st = app.pausa;
  if (!st) return '';
  const c = humano();
  const resto = Math.ceil(restoDaPausa() / 1000);
  // coordenadas do campo: a da formação em campo (senão uma linha de 11)
  const slotsView = slotsMostrados(st);
  const coords = st.formacao ? posicoesNoCampo(st.formacao) : slotsView.map((_, i) => ({ x: 5 + i * 9, y: 50 }));

  const campo = slotsView
    .map((slot, k) => {
      const u = st.perm[k] ?? k;
      const original = st.ocupantes[u] ?? null;
      const p = coords[k] ?? { x: 50, y: 50 };
      const troca = original === null ? undefined : st.pendentes.find((x) => x.saiId === original);
      const mostrado = jogadorDe(c, troca ? troca.entraId : original);
      // data-i: slot da tela (alvo do arrastar); data-id: quem estava ali antes (a troca é por ele)
      if (!mostrado) return `<button type="button" class="slot-campo vazio" style="left:${p.x}%;top:${p.y}%" data-i="${k}" disabled>${camisaVazia()}<span class="jf">${slot}</span></button>`;
      const sel = original !== null && st.selSai === original ? ' sel' : '';
      return `<button type="button" class="slot-campo${sel}${troca ? ' trocado' : ''}" style="left:${p.x}%;top:${p.y}%" data-a="pausa-sai" data-i="${k}" data-id="${original ?? ''}">${shirt(c, mostrado, slot)}${troca ? '<span class="insignias">entra</span>' : ''}${rotuloJogador(mostrado, slot)}</button>`;
    })
    .join('');

  const usados = new Set(st.pendentes.map((x) => x.entraId));
  const banco = st.banco
    .map((id) => jogadorDe(c, id))
    .filter((j): j is Jogador => Boolean(j))
    .sort((a, b) => b.overall - a.overall)
    .map((j) => `<button type="button" class="slot-disp${usados.has(j.id) ? ' usado' : ''}" data-a="pausa-entra" data-id="${j.id}" ${usados.has(j.id) ? 'disabled' : ''}>${shirt(c, j, null)}${rotuloJogador(j, null)}</button>`)
    .join('');

  const pend = st.pendentes
    .map((x) => {
      const sai = jogadorDe(c, x.saiId);
      const entra = jogadorDe(c, x.entraId);
      return `<li>${esc(sai?.nome ?? '?')} <b>→</b> ${esc(entra?.nome ?? '?')} <button type="button" class="btn peq" data-a="pausa-desfazer" data-id="${x.saiId}" aria-label="Desfazer">✕</button></li>`;
    })
    .join('');

  const opc = (valor: string, itens: [string, string][]): string => itens.map(([v, n]) => `<option value="${v}" ${valor === v ? 'selected' : ''}>${n}</option>`).join('');
  const urgente = resto <= 10 ? ' urgente' : '';
  const adv = (() => {
    const p = partidaDoClube(c.id);
    if (!p) return '';
    return clubePorId(jogoAtual(), p.mandante === c.id ? p.visitante : p.mandante).nome;
  })();

  const iv = intervaloAtivo();
  const espera = iv ? iv.esperados.filter((id) => !iv.prontos.includes(id)).length : 0;
  const cabecalho = st.local
    ? `<b>⏸ Jogo pausado aos ${st.minuto}'</b> contra ${esc(adv)} — sem limite de tempo: troque quem quiser e mude a tática; o jogo volta de onde parou.`
    : st.hidratacao
    ? `<b>💧 Pausa para hidratação aos ${st.minuto}'</b> contra ${esc(adv)} — dá para ajustar o time. O jogo volta quando todos confirmam (faltam ${espera}).`
    : st.intervalo
      ? `<b>⏸ Intervalo</b> contra ${esc(adv)} — escale o 2º tempo. O jogo volta quando todos confirmam (faltam ${espera}).`
      : `<b>⏸ Jogo pausado aos ${st.minuto}'</b> contra ${esc(adv)}`;
  return dialogo(
    st.hidratacao ? 'Pausa para hidratação' : st.intervalo ? 'Intervalo' : 'Jogo pausado',
    `<div class="pausa-cab${urgente}">${cabecalho}${st.local ? '' : `<span class="pausa-tempo" title="O servidor espera ${(st.hidratacao ? HIDRATACAO_MS : st.intervalo ? INTERVALO_MS : PAUSA_MS) / 1000}s">${resto}s</span>`}</div>
    <div class="esc-grade">
      <div class="esc-esq"><div class="campo-fut">${campo}</div>
        <p class="cinza peq">Clique no jogador que sai e depois em quem entra, no banco, ou arraste (um reserva sobre um titular troca os dois; um titular sobre outro muda a posição; arraste um titular para o banco para desfazer a troca). Substituições restantes: <b>${st.restantes - st.pendentes.length}</b> de 5.</p></div>
      <div class="esc-dir">
        <div class="pn-tit verde"><span>Banco de reservas</span></div>
        <div class="disponiveis">${banco || '<span class="cinza">Sem reservas disponíveis.</span>'}</div>
        <div class="pn-tit"><span>Trocas desta pausa</span></div>
        <ul class="pausa-trocas">${pend || '<li class="cinza">Nenhuma troca.</li>'}</ul>
        <div class="controles-esc">
          <label>Formação:<select data-ch="pz-formacao" ${st.formacao ? '' : 'disabled'}>${st.formacao ? '' : '<option value="">(personalizada)</option>'}${LISTA_FORMACOES.map((x) => `<option value="${x}" ${st.formacao === x ? 'selected' : ''}>${x}</option>`).join('')}</select></label>
          <label>Mentalidade:<select data-ch="pz-estilo">${opc(st.estilo, LISTA_ESTILOS.map((x): [string, string] => [x, ROTULO_ESTILO[x]]))}</select></label>
          <label>Passe:<select data-ch="pz-passe">${opc(st.passe, LISTA_PASSES.map((x): [string, string] => [x, ROTULO_PASSE[x]]))}</select></label>
          <label>Pressão:<select data-ch="pz-pressao">${opc(st.pressao, LISTA_PRESSOES.map((x): [string, string] => [x, ROTULO_PRESSAO[x]]))}</select></label>
          <label>Agressividade da marcação:<select data-ch="pz-marcacao">${opc(st.marcacao, [['leve', 'Cautelosa'], ['normal', 'Normal'], ['forte', 'Agressiva (carrinhos)']])}</select></label>
          <label>Concentrar ataques:<select data-ch="pz-ataque">${opc(st.ataque, [['meio', 'Pelo meio'], ['lados', 'Pelos lados'], ['equilibrado', 'Equilibrado']])}</select></label>
          <p class="cinza peq dica-tatica">${esc(DICA_ESTILO[st.estilo])} ${esc(DICA_PASSE[st.passe])} ${esc(DICA_PRESSAO[st.pressao])}</p>
        </div>
        <div class="dlg-botoes"><button class="btn principal" data-a="pausa-ok">${st.local || st.hidratacao ? 'Confirmar e voltar ao jogo' : st.intervalo ? 'Confirmar escalação do 2º tempo' : 'Confirmar e voltar ao jogo'}</button></div>
      </div></div>`,
    'escalar pausa',
    'pausa-ok',
  );
}

export function pausaClique(acao: string, d: DOMStringMap): void {
  const st = app.pausa;
  if (!st) return;
  const c = humano();
  const id = Number(d.id);
  app.mensagem = '';
  if (acao === 'pausa-sai') {
    // clicar de novo no mesmo jogador ou em quem já tem troca marcada desfaz
    if (st.pendentes.some((x) => x.saiId === id)) st.pendentes = st.pendentes.filter((x) => x.saiId !== id);
    else st.selSai = st.selSai === id ? null : id;
  } else if (acao === 'pausa-entra') {
    if (st.selSai === null) {
      app.mensagem = 'Escolha primeiro quem sai (no campo).';
      return;
    }
    if (st.pendentes.length >= st.restantes) {
      app.mensagem = 'Não há mais substituições.';
      return;
    }
    const sai = jogadorDe(c, st.selSai);
    const entra = jogadorDe(c, id);
    const posSai = st.slots[st.ocupantes.indexOf(st.selSai)];
    if (!sai || !entra) return;
    if ((posSai === 'G') !== (entra.posicao === 'G')) {
      app.mensagem = 'Goleiro só é trocado por outro goleiro.';
      return;
    }
    st.pendentes.push({ saiId: st.selSai, entraId: id });
    st.selSai = null;
  } else if (acao === 'pausa-desfazer') {
    st.pendentes = st.pendentes.filter((x) => x.saiId !== id);
  }
}

export function pausaMudar(campo: string, valor: string): void {
  const st = app.pausa;
  if (!st) return;
  if (campo === 'pz-formacao') {
    if ((LISTA_FORMACOES as string[]).includes(valor)) refazerPerm(st, valor as Formacao);
  } else if (campo === 'pz-estilo') st.estilo = valor as EstadoPausa['estilo'];
  else if (campo === 'pz-passe') st.passe = valor as EstadoPausa['passe'];
  else if (campo === 'pz-pressao') st.pressao = valor as EstadoPausa['pressao'];
  else if (campo === 'pz-marcacao') st.marcacao = valor as EstadoPausa['marcacao'];
  else if (campo === 'pz-ataque') st.ataque = valor as EstadoPausa['ataque'];
}

/** Arrastar e soltar na janela de pausa/intervalo: mesmas regras do clicar e clicar, mais a troca de posição entre titulares. */
export function soltarPausa(o: OrigemArraste, a: AlvoArraste): void {
  const st = app.pausa;
  if (!st) return;
  app.mensagem = '';
  const slotsView = slotsMostrados(st);
  if (o.tipo === 'slot' && a.tipo === 'slot') {
    if (o.i === a.i) return;
    if (slotsView[o.i] === 'G' || slotsView[a.i] === 'G') {
      app.mensagem = 'O goleiro não muda de posição.';
      return;
    }
    const x = st.perm[o.i] as number;
    st.perm[o.i] = st.perm[a.i] as number;
    st.perm[a.i] = x;
    return;
  }
  if (o.tipo === 'disp' && a.tipo === 'slot') {
    const original = st.ocupantes[st.perm[a.i] as number] ?? null;
    if (original === null) {
      app.mensagem = 'Não há ninguém nessa posição para sair.';
      return;
    }
    const antes = [...st.pendentes];
    st.pendentes = st.pendentes.filter((x) => x.saiId !== original);
    st.selSai = original;
    pausaClique('pausa-entra', { id: String(o.id) } as DOMStringMap);
    if (app.mensagem) {
      st.pendentes = antes;
      st.selSai = null;
    }
    return;
  }
  if (o.tipo === 'slot' && a.tipo === 'disp') {
    const original = st.ocupantes[st.perm[o.i] as number] ?? null;
    if (original !== null) st.pendentes = st.pendentes.filter((x) => x.saiId !== original);
    st.selSai = null;
  }
}

/** Confere o relógio do servidor: segundos que faltam também alimentam o texto "volta em Ns" dos outros técnicos. */
export function decorridoDaRodada(): number {
  const o = app.online;
  const rel = o?.visao?.sala.aoVivo;
  if (!o?.visao || !rel) return Number.POSITIVE_INFINITY;
  return decorridoMs(rel, agoraDoServidor());
}
